/**
 * Onboarding — 5-step first-launch wizard
 *
 * Step 0: Training Goal     (strength / hypertrophy / general fitness)
 * Step 1: Experience        (beginner / intermediate / advanced)
 * Step 2: Equipment         (multi-select: what you have at your gym)
 * Step 3: Session structure (duration + split type)
 * Step 4: Starting weights  (optional calibration for the big 5 lifts)
 *
 * Writes to AppSettings + GymProfile + seeds initial LoggedSets on completion.
 */

import { useState } from 'react';
import { repository } from '@/lib/DexieRepository';
import { db } from '@/lib/db';
import type {
  TrainingGoal,
  ExperienceLevel,
  PreferredDuration,
  SplitType,
  SplitDay,
  Equipment,
} from '@/types';

// ── Step definitions ──────────────────────────────────────────

interface GoalOption {
  value: TrainingGoal;
  label: string;
  emoji: string;
  desc: string;
  repRange: string;
}

const GOALS: GoalOption[] = [
  {
    value: 'strength',
    label: 'Strength',
    emoji: '🏋️',
    desc: 'Maximise your 1RM on the big lifts',
    repRange: '1–5 reps · High intensity',
  },
  {
    value: 'hypertrophy',
    label: 'Muscle Growth',
    emoji: '💪',
    desc: 'Build size with progressive volume',
    repRange: '6–12 reps · Moderate intensity',
  },
  {
    value: 'general_fitness',
    label: 'General Fitness',
    emoji: '⚡',
    desc: 'Balanced training, stay active',
    repRange: '10–15 reps · Circuit style',
  },
];

interface LevelOption {
  value: ExperienceLevel;
  label: string;
  emoji: string;
  desc: string;
}

const LEVELS: LevelOption[] = [
  { value: 'beginner',     label: 'Beginner',     emoji: '🌱', desc: 'Less than 1 year of consistent training' },
  { value: 'intermediate', label: 'Intermediate', emoji: '📈', desc: '1–3 years of consistent training' },
  { value: 'advanced',     label: 'Advanced',     emoji: '🔥', desc: '3+ years, know your way around a barbell' },
];

const DURATIONS: { value: PreferredDuration; label: string; desc: string }[] = [
  { value: 30, label: '30 min', desc: 'Quick, focused' },
  { value: 45, label: '45 min', desc: 'Efficient' },
  { value: 60, label: '60 min', desc: 'Standard' },
  { value: 75, label: '75 min', desc: 'Extended' },
];

interface EquipmentOption {
  value: Equipment;
  label: string;
  emoji: string;
}

const EQUIPMENT_OPTIONS: EquipmentOption[] = [
  { value: 'barbell',    label: 'Barbell',     emoji: '🏋️' },
  { value: 'dumbbell',   label: 'Dumbbells',   emoji: '💪' },
  { value: 'cable',      label: 'Cable',       emoji: '🔗' },
  { value: 'machine',    label: 'Machines',    emoji: '⚙️' },
  { value: 'kettlebell', label: 'Kettlebells', emoji: '🔔' },
  { value: 'bodyweight', label: 'Bodyweight',  emoji: '🤸' },
  { value: 'bands',      label: 'Bands',       emoji: '🎗️' },
  { value: 'ez_bar',     label: 'EZ Bar',      emoji: '〰️' },
  { value: 'trap_bar',   label: 'Trap Bar',    emoji: '🔷' },
];

const SPLITS: { value: SplitType; label: string; emoji: string; desc: string }[] = [
  { value: 'push_pull',      emoji: '🔄', label: 'Push / Pull',       desc: 'Push → Pull, alternating' },
  { value: 'push_pull_legs', emoji: '🔁', label: 'Push / Pull / Legs', desc: '3-day cycle with dedicated leg day' },
  { value: 'upper_lower',    emoji: '↕️', label: 'Upper / Lower',      desc: 'Upper body alternates with lower' },
  { value: 'full_body',      emoji: '⚡', label: 'Full Body',          desc: 'Hit everything every session' },
];

const SPLIT_FIRST_DAY: Record<SplitType, SplitDay> = {
  push_pull:      'push',
  push_pull_legs: 'push',
  upper_lower:    'upper',
  full_body:      'full_body_day',
};

// Big 5 for weight seeder
const SEED_LIFTS = [
  { exerciseId: 'ex_bp_barbell',    label: 'Bench Press',    equipment: 'barbell' as Equipment },
  { exerciseId: 'ex_squat_barbell', label: 'Back Squat',     equipment: 'barbell' as Equipment },
  { exerciseId: 'ex_deadlift',      label: 'Deadlift',       equipment: 'barbell' as Equipment },
  { exerciseId: 'ex_ohp_barbell',   label: 'Overhead Press', equipment: 'barbell' as Equipment },
  { exerciseId: 'ex_row_barbell',   label: 'Bent-Over Row',  equipment: 'barbell' as Equipment },
];

// ── Step indicator ────────────────────────────────────────────

function StepIndicator({ current, total }: { current: number; total: number }) {
  return (
    <div className="flex gap-2 justify-center mb-8">
      {Array.from({ length: total }, (_, i) => (
        <div
          key={i}
          className={`h-1.5 rounded-full transition-all duration-300
            ${i < current ? 'w-6 bg-accent' : i === current ? 'w-8 bg-accent' : 'w-6 bg-surface-400'}`}
        />
      ))}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────

interface OnboardingProps {
  onComplete: () => void;
}

export default function Onboarding({ onComplete }: OnboardingProps) {
  const [step, setStep]           = useState(0);
  const [goal, setGoal]           = useState<TrainingGoal>('hypertrophy');
  const [level, setLevel]         = useState<ExperienceLevel>('intermediate');
  const [equipment, setEquipment] = useState<Set<Equipment>>(
    new Set(['barbell', 'dumbbell', 'cable', 'machine'])
  );
  const [duration, setDuration]   = useState<PreferredDuration>(60);
  const [splitType, setSplitType] = useState<SplitType>('push_pull');
  const [weights, setWeights]     = useState<Record<string, string>>(
    Object.fromEntries(SEED_LIFTS.map((l) => [l.exerciseId, '']))
  );
  const [saving, setSaving]       = useState(false);

  const TOTAL_STEPS = 5;

  const toggleEquipment = (eq: Equipment) => {
    setEquipment((prev) => {
      const next = new Set(prev);
      if (next.has(eq)) {
        if (next.size > 1) next.delete(eq); // keep at least one
      } else {
        next.add(eq);
      }
      return next;
    });
  };

  const handleFinish = async () => {
    setSaving(true);
    const volumeTarget = goal === 'strength' ? 10 : goal === 'hypertrophy' ? 15 : 12;
    const setCapByLevel = level === 'beginner' ? 18 : level === 'intermediate' ? 22 : 26;

    // Update gym profile with selected equipment
    const profiles = await db.gymProfiles.toArray();
    const activeProfile = profiles.find((p) => p.isActive) ?? profiles[0];
    if (activeProfile) {
      await db.gymProfiles.update(activeProfile.id, {
        availableEquipment: [...equipment],
      });
    }

    // Save settings
    await repository.updateSettings({
      trainingGoal: goal,
      experienceLevel: level,
      preferredDurationMinutes: duration,
      splitType,
      nextSplitDay: SPLIT_FIRST_DAY[splitType],
      weeklyVolumeTarget: volumeTarget,
      sessionSetCap: setCapByLevel,
      onboardingComplete: true,
    });

    // Seed starting weights if any were entered
    const anyFilled = Object.values(weights).some((v) => parseFloat(v) > 0);
    if (anyFilled) {
      const now = new Date().toISOString();
      const workoutId = 'seed_' + Date.now();
      await db.workouts.put({
        id: workoutId, startedAt: now, completedAt: now,
        splitDay: 'anterior', status: 'completed',
        gymProfileId: activeProfile?.id ?? 'profile_default', durationSeconds: 0,
      });
      for (const lift of SEED_LIFTS) {
        const raw = parseFloat(weights[lift.exerciseId]);
        if (!raw || isNaN(raw)) continue;
        await db.loggedSets.put({
          id: 'seed_' + lift.exerciseId,
          workoutId, exerciseId: lift.exerciseId,
          setNumber: 1, weightLbs: raw, reps: 5,
          rpe: 7, rir: 3, loggedAt: now,
          isWarmup: false,
          estimated1RM: raw * (1 + 5 / 30),
        });
      }
    }

    setSaving(false);
    onComplete();
  };

  const hasBarbellEquipment = equipment.has('barbell');

  return (
    <div className="fixed inset-0 z-50 bg-surface flex flex-col overflow-y-auto pb-safe">
      <div className="flex-1 px-6 pt-16 pb-8 max-w-md mx-auto w-full">

        {/* Logo */}
        <div className="flex items-center gap-3 mb-10">
          <div className="w-10 h-10 rounded-2xl bg-accent flex items-center justify-center">
            <svg viewBox="0 0 24 24" className="w-6 h-6 text-white" fill="none"
              stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round"
                d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
            </svg>
          </div>
          <div>
            <p className="font-bold text-ink text-lg leading-none">FlexEngine</p>
            <p className="text-ink-faint text-xs">Let's build your program</p>
          </div>
        </div>

        <StepIndicator current={step} total={TOTAL_STEPS} />

        {/* ── Step 0: Goal ───────────────────────────────────── */}
        {step === 0 && (
          <div className="animate-fade-in space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-ink tracking-tight">What's your goal?</h2>
              <p className="text-ink-muted text-sm mt-1">
                This shapes your rep ranges, rest times, and volume targets.
              </p>
            </div>
            <div className="space-y-3">
              {GOALS.map((g) => (
                <button
                  key={g.value}
                  onClick={() => setGoal(g.value)}
                  className={`w-full text-left p-4 rounded-2xl border-2 transition-all duration-150
                               active:scale-[0.98]
                    ${goal === g.value
                      ? 'border-accent bg-accent/10'
                      : 'border-surface-400 bg-surface-200 hover:border-surface-500'}`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{g.emoji}</span>
                    <div className="flex-1">
                      <p className="font-bold text-ink">{g.label}</p>
                      <p className="text-ink-muted text-sm">{g.desc}</p>
                    </div>
                    {goal === g.value && (
                      <div className="w-5 h-5 rounded-full bg-accent flex items-center
                                      justify-center text-white text-xs font-bold">✓</div>
                    )}
                  </div>
                  <p className="text-xs text-ink-faint mt-2 ml-9">{g.repRange}</p>
                </button>
              ))}
            </div>
            <button className="btn-primary w-full py-4" onClick={() => setStep(1)}>
              Continue →
            </button>
          </div>
        )}

        {/* ── Step 1: Experience ─────────────────────────────── */}
        {step === 1 && (
          <div className="animate-fade-in space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-ink tracking-tight">
                How long have you been training?
              </h2>
              <p className="text-ink-muted text-sm mt-1">
                Affects starting weights, recovery time, and weekly volume.
              </p>
            </div>
            <div className="space-y-3">
              {LEVELS.map((l) => (
                <button
                  key={l.value}
                  onClick={() => setLevel(l.value)}
                  className={`w-full text-left p-4 rounded-2xl border-2 transition-all duration-150
                               active:scale-[0.98]
                    ${level === l.value
                      ? 'border-accent bg-accent/10'
                      : 'border-surface-400 bg-surface-200 hover:border-surface-500'}`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{l.emoji}</span>
                    <div className="flex-1">
                      <p className="font-bold text-ink">{l.label}</p>
                      <p className="text-ink-muted text-sm">{l.desc}</p>
                    </div>
                    {level === l.value && (
                      <div className="w-5 h-5 rounded-full bg-accent flex items-center
                                      justify-center text-white text-xs font-bold">✓</div>
                    )}
                  </div>
                </button>
              ))}
            </div>
            <div className="flex gap-3">
              <button className="btn-secondary flex-1" onClick={() => setStep(0)}>← Back</button>
              <button className="btn-primary flex-1 py-4" onClick={() => setStep(2)}>Continue →</button>
            </div>
          </div>
        )}

        {/* ── Step 2: Equipment ──────────────────────────────── */}
        {step === 2 && (
          <div className="animate-fade-in space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-ink tracking-tight">
                What equipment do you have?
              </h2>
              <p className="text-ink-muted text-sm mt-1">
                The generator only suggests exercises you can actually do.
              </p>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {EQUIPMENT_OPTIONS.map((eq) => {
                const active = equipment.has(eq.value);
                return (
                  <button
                    key={eq.value}
                    onClick={() => toggleEquipment(eq.value)}
                    className={`flex flex-col items-center gap-1.5 p-3 rounded-2xl border-2
                                transition-all duration-150 active:scale-[0.97]
                      ${active
                        ? 'border-accent bg-accent/10'
                        : 'border-surface-400 bg-surface-200 hover:border-surface-500'}`}
                  >
                    <span className="text-2xl">{eq.emoji}</span>
                    <span className={`text-xs font-semibold ${active ? 'text-accent' : 'text-ink-muted'}`}>
                      {eq.label}
                    </span>
                    {active && (
                      <span className="text-accent text-xs font-bold">✓</span>
                    )}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-ink-faint text-center">
              {equipment.size} selected · tap to toggle
            </p>
            <div className="flex gap-3">
              <button className="btn-secondary flex-1" onClick={() => setStep(1)}>← Back</button>
              <button className="btn-primary flex-1 py-4" onClick={() => setStep(3)}>Continue →</button>
            </div>
          </div>
        )}

        {/* ── Step 3: Duration + Split ───────────────────────── */}
        {step === 3 && (
          <div className="animate-fade-in space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-ink tracking-tight">
                Structure your sessions
              </h2>
              <p className="text-ink-muted text-sm mt-1">
                Duration caps your session length. Split determines how muscle groups cycle.
              </p>
            </div>

            {/* Duration */}
            <div>
              <p className="text-xs text-ink-faint uppercase tracking-widest mb-2">Session Length</p>
              <div className="grid grid-cols-4 gap-2">
                {DURATIONS.map((d) => (
                  <button
                    key={d.value}
                    onClick={() => setDuration(d.value)}
                    className={`flex flex-col items-center p-3 rounded-2xl border-2 transition-all
                                active:scale-[0.97]
                      ${duration === d.value
                        ? 'border-accent bg-accent/10'
                        : 'border-surface-400 bg-surface-200'}`}
                  >
                    <p className={`text-base font-bold ${duration === d.value ? 'text-accent' : 'text-ink'}`}>
                      {d.label}
                    </p>
                    <p className="text-xs text-ink-faint mt-0.5">{d.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Split type */}
            <div>
              <p className="text-xs text-ink-faint uppercase tracking-widest mb-2">Training Split</p>
              <div className="space-y-2">
                {SPLITS.map((s) => (
                  <button
                    key={s.value}
                    onClick={() => setSplitType(s.value)}
                    className={`w-full flex items-center gap-3 p-3.5 rounded-2xl border-2
                                transition-all active:scale-[0.98]
                      ${splitType === s.value
                        ? 'border-accent bg-accent/10'
                        : 'border-surface-400 bg-surface-200'}`}
                  >
                    <span className="text-xl">{s.emoji}</span>
                    <div className="flex-1 text-left">
                      <p className={`font-semibold text-sm ${splitType === s.value ? 'text-accent' : 'text-ink'}`}>
                        {s.label}
                      </p>
                      <p className="text-xs text-ink-muted">{s.desc}</p>
                    </div>
                    {splitType === s.value && (
                      <span className="text-accent font-bold">✓</span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-3">
              <button className="btn-secondary flex-1" onClick={() => setStep(2)}>← Back</button>
              <button className="btn-primary flex-1 py-4" onClick={() => setStep(4)}>Continue →</button>
            </div>
          </div>
        )}

        {/* ── Step 4: Starting weights (optional) ────────────── */}
        {step === 4 && (
          <div className="animate-fade-in space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-ink tracking-tight">
                Calibrate your starting weights
              </h2>
              <p className="text-ink-muted text-sm mt-1">
                Optional. Enter your current working weight for any lifts you know.
                FlexEngine uses these to suggest progressive overload targets from session one.
              </p>
            </div>

            {hasBarbellEquipment ? (
              <div className="card space-y-3">
                {SEED_LIFTS.map((lift) => (
                  <div key={lift.exerciseId} className="flex items-center gap-3">
                    <span className="flex-1 text-sm text-ink">{lift.label}</span>
                    <div className="flex items-center gap-1.5">
                      <input
                        type="number"
                        inputMode="decimal"
                        placeholder="—"
                        value={weights[lift.exerciseId]}
                        onChange={(e) => setWeights((p) => ({ ...p, [lift.exerciseId]: e.target.value }))}
                        className="w-20 bg-surface-300 rounded-xl text-center text-sm font-mono
                                   font-bold text-ink py-2.5 focus:outline-none focus:ring-2
                                   focus:ring-accent min-h-tap"
                      />
                      <span className="text-xs text-ink-faint w-6">lbs</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="card text-center py-6 space-y-2">
                <p className="text-2xl">🤸</p>
                <p className="text-sm text-ink-muted">
                  No barbell selected — weight calibration uses barbell lifts.
                  You can always add starting weights later from Settings.
                </p>
              </div>
            )}

            {/* Summary card */}
            <div className="card bg-surface-200 space-y-2">
              <p className="text-xs text-ink-muted font-medium uppercase tracking-widest mb-3">
                Your Program
              </p>
              {[
                { label: 'Goal',      value: GOALS.find(g => g.value === goal)?.label ?? '' },
                { label: 'Level',     value: LEVELS.find(l => l.value === level)?.label ?? '' },
                { label: 'Equipment', value: `${equipment.size} types` },
                { label: 'Duration',  value: `${duration} min` },
                { label: 'Split',     value: SPLITS.find(s => s.value === splitType)?.label ?? '' },
              ].map(({ label, value }) => (
                <div key={label} className="flex justify-between items-center">
                  <span className="text-sm text-ink-muted">{label}</span>
                  <span className="text-sm font-semibold text-ink">{value}</span>
                </div>
              ))}
            </div>

            <div className="flex gap-3">
              <button className="btn-secondary flex-1" onClick={() => setStep(3)}>← Back</button>
              <button
                className="btn-primary flex-1 py-4"
                onClick={handleFinish}
                disabled={saving}
              >
                {saving ? 'Saving…' : "Let's Go 🚀"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
